// ============================================================
// call_api args 사전 정규화 (deterministic guard)
// ------------------------------------------------------------
// 본질: backend strict match 인텐션에서 LLM이 user input의 한국어 단위 suffix
//   ("번", "호", "번호", "호실")를 그대로 param에 pass-through 시 매칭 실패.
//   예: CHECK_LOCKER_DETAIL lockerName="22번"
//       → backend 저장값 "22" 와 strict mismatch → "Locker not found"
//
// 가이드(LLM 의존)만으로는 결정성 미흡. 코드 결정적 가드로 차단.
// 적용 범위: CALL_API_ARG_NORMALIZE에 등록된 intention의 등록된 param만.
//   다른 intention/param 영향 0.
//
// [L18-SMS-FIRST-TURN-001] EXPIRING_LIST date range guard:
//   LLM이 startDate===endDate 동일 날짜(당일만 만료)로 보내면 0건 응답 →
//   "만료 예정" 본래 의미(향후 14일 이내) 충족 불가. defaultPeriod today~+14d 강제.
//   사용자 명시 기간("3월 만료", "다음달 만료")은 startDate≠endDate이므로 영향 0.
// ============================================================

export function stripKoreanUnitSuffix(v) {
  if (typeof v !== 'string') return v;
  return v.replace(/(번호|호실|번|호)$/u, '').trim();
}

// backend lockerName 은 raw 숫자 문자열 ("1"~"50") 저장. LLM이 user input의
// "22번", "22번 락커", "락커 22번" 등 형태로 보내면 strict mismatch.
// 숫자 substring 추출로 정규화 (없으면 원본 fallback).
export function extractLockerNameNumber(v) {
  if (typeof v !== 'string') return v;
  const m = v.match(/\d+/);
  return m ? m[0] : v;
}

// YYYY-MM-DD 포맷 헬퍼 //
function _fmtYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// [운영 5차 rid162] 사용자가 "오늘만/오늘 하루/당일/14일 말고" 등 단일-당일 의도를 명시하면 same-day 확장 skip.
//   근본: 아래 same-day 가드는 bare "만료 예정" 질의(LLM이 today~today 기본값 → 0건)를 구제하기 위한 것. 그러나
//   사용자가 "오늘만"을 명시하면 LLM의 today~today는 의도적이므로 확장하면 안 됨(운영 rid162: "14일 말고 오늘만!"인데
//   가드가 06-26~06-26 → 06-26~07-10 확장 → 오늘 아닌 D-1 39명 오응답). "만료 예정"만 있는 bare 질의는 이 마커가
//   없으므로 기존 확장 유지. "오늘" reference 단어가 있어도 LLM이 range로 보내면 same-day 아니라 이 분기 미도달.
const EXPLICIT_SAMEDAY_RE = /오늘|당일|금일|하루\s*(?:만|남|밖|뿐)/;

// EXPIRING_LIST date range 정규화 — 동일 날짜(startDate===endDate) 시 defaultPeriod 적용 //
// "만료 예정 회원" 질의에서 LLM이 today~today(당일만)로 보내면 0건 응답하는 비결정성 차단.
// 사용자가 특정 기간 명시("3월 만료", "이번달 만료")하면 startDate≠endDate이므로 영향 없음.
export function normalizeExpiringDateRange(params, userInput) {
  if (!params || typeof params !== 'object') return params;

  const { startDate, endDate } = params;
  const today = new Date();
  const todayStr = _fmtYMD(today);
  const plus14 = new Date(today);

  plus14.setDate(plus14.getDate() + 14);
  const plus14Str = _fmtYMD(plus14);

  // 날짜 미지정 — defaultPeriod(today~+14d) 자동 채움 (backend default 의존 제거) //
  if (!startDate && !endDate) {
    params.startDate = todayStr;
    params.endDate = plus14Str;
    console.log(`[call_api] arg normalize: EXPIRING_LIST dateRange (missing) → "${todayStr}~${plus14Str}" (default fill)`);
    return params;
  }

  // 동일 날짜 — defaultPeriod(today~+14d)로 확장 (단, 사용자 당일-only 명시 / 오늘 아닌 특정일이면 skip) //
  // 본 확장의 목적은 bare "만료 예정" 질의에서 LLM이 today~today(기본값)로 보내 0건 나는 것을 구제하는 것.
  //   ∴ (a) 사용자가 "오늘만" 명시 → 확장 금지(rid162) (b) same-day가 오늘 아닌 특정일(예: "3월 1일 만료") → 존중.
  //   확장은 오직 "same-day == 오늘 + 당일-only 마커 없음"(=LLM 기본값 추정) 일 때만.
  if (startDate && endDate && startDate === endDate) {
    if (typeof userInput === 'string' && EXPLICIT_SAMEDAY_RE.test(userInput)) {
      console.log(`[call_api] arg normalize: EXPIRING_LIST same-day expansion SKIPPED "${startDate}~${endDate}" (explicit today-only intent: "${userInput.slice(0, 30)}")`);
      return params;
    }
    if (startDate !== todayStr) {
      console.log(`[call_api] arg normalize: EXPIRING_LIST same-day kept "${startDate}~${endDate}" (specific single day, not today)`);
      return params;
    }
    params.startDate = todayStr;
    params.endDate = plus14Str;
    console.log(`[call_api] arg normalize: EXPIRING_LIST dateRange "${startDate}~${endDate}" → "${todayStr}~${plus14Str}" (same-day guard)`);
  }

  return params;
}

const GENERIC_PERSON_NOUNS_RE = /^(회원|멤버|사람|고객|이용자|유저|member|사용자|이름)$/i;

export function normalizeMemberSearchParams(params) {
  if (!params || typeof params !== 'object') return params;

  // [운영 5차 rid135] 전화 끝자리(부분 숫자) 검색 — LLM이 부분 숫자("4805")를 name/keyword에 넣으면
  //   이름 검색으로 처리돼 실패. 순수 숫자열(2자리+)은 phone 파라미터로 라우팅 → 백엔드 /api2/member/search가
  //   phone 끝자리(LIKE '%입력', 커밋 aa22c4b) 매칭. phone 이미 있으면 유지. 하이픈/공백 포함 숫자도 정규화.
  const _digits = (s) => (typeof s === 'string' ? s.replace(/[\s-]/g, '') : '');
  if (!params.phone) {
    const _nameDigits = _digits(params.name);
    const _kwDigits = _digits(params.keyword);
    if (params.name != null && /^\d{2,}$/.test(_nameDigits)) {
      params.phone = _nameDigits;
      delete params.name;
      console.log(`[call_api] arg normalize: CHECK_MEMBER_SEARCH numeric name→phone: phone="${params.phone}" (전화 끝자리 검색)`);
    } else if (params.keyword != null && /^\d{2,}$/.test(_kwDigits)) {
      params.phone = _kwDigits;
      delete params.keyword;
      console.log(`[call_api] arg normalize: CHECK_MEMBER_SEARCH numeric keyword→phone: phone="${params.phone}" (전화 끝자리 검색)`);
    }
  }

  if (params.keyword && (!params.name || GENERIC_PERSON_NOUNS_RE.test(params.name))) {
    const prev = params.name;
    params.name = params.keyword;
    delete params.keyword;
    console.log(`[call_api] arg normalize: CHECK_MEMBER_SEARCH keyword→name migration: name="${prev}"→"${params.name}" (keyword removed)`);
    return params;
  }

  if (params.name && GENERIC_PERSON_NOUNS_RE.test(params.name)) {
    console.log(`[call_api] arg normalize: CHECK_MEMBER_SEARCH generic noun rejected: name="${params.name}"`);
    delete params.name;
  }

  if (params.keyword) {
    delete params.keyword;
  }

  return params;
}

export const CALL_API_ARG_NORMALIZE = {
  'CHECK_LOCKER_DETAIL': { lockerName: extractLockerNameNumber },
  'CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD': { _params: normalizeExpiringDateRange },
  'CHECK_MEMBER_SEARCH': { _params: normalizeMemberSearchParams },
};

// intention 별 등록된 param에 normalizer 적용 (in-place mutate).
// 변경 발생 시 console.log로 추적. 등록 외 param/intention은 그대로 유지.
// _params 키: params 객체 전체를 받아 multi-param 교차 정규화 (예: date range guard).
export function normalizeCallApiArgs(intention, params, userInput) {
  if (!params || typeof params !== 'object') return params;
  const normalizers = CALL_API_ARG_NORMALIZE[intention];
  if (!normalizers) return params;

  // _params: 전체 params 객체 정규화 (multi-param 교차 검증용). userInput 전달 — rid162 당일-only 의도 판정용 //
  if (normalizers._params) {
    normalizers._params(params, userInput);
  }

  for (const pname of Object.keys(normalizers)) {
    if (pname === '_params') continue;
    if (pname in params) {
      const orig = params[pname];
      const next = normalizers[pname](orig);
      if (next !== orig) {
        params[pname] = next;
        console.log(`[call_api] arg normalize: ${intention}.${pname} "${orig}" → "${next}"`);
      }
    }
  }
  return params;
}
